import { Widget } from '@/shared/components';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ChatPanel } from '../chat-panel/chat-panel';
import { BotConfigService } from '../../services';

const CLOSE_ANIMATION_MS = 300;

@Component({
  selector: 'bevvi-chat-widget',
  imports: [Widget, ChatPanel],
  templateUrl: './chat-widget.html',
})
export class ChatWidget implements OnInit, OnDestroy {
  protected readonly opened = signal(false);
  protected readonly closing = signal(false);

  private readonly botConfigService = inject(BotConfigService);
  private closeTimeoutId?: ReturnType<typeof setTimeout>;

  private readonly onMessage = (event: MessageEvent): void => {
    if (event.source !== globalThis.parent) return;

    const expectedOrigin = globalThis.document.referrer
      ? new URL(globalThis.document.referrer).origin
      : null;
    if (expectedOrigin && event.origin !== expectedOrigin) return;

    if (event.data?.type === 'bevvi:open') this.openChat();
    if (event.data?.type === 'bevvi:close') this.closeChat();
    if (event.data?.type === 'bevvi:toggle') this.toggleChat();
  };

  ngOnInit(): void {
    window.addEventListener('message', this.onMessage);

    const botId = new URLSearchParams(globalThis.location.search).get('botId');
    if (botId) {
      this.botConfigService.load(botId);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    clearTimeout(this.closeTimeoutId);
  }

  public toggleChat(): void {
    if (this.opened()) {
      this.closeChat();
    } else {
      this.openChat();
    }
  }

  private openChat(): void {
    clearTimeout(this.closeTimeoutId);
    this.closing.set(false);
    this.opened.set(true);
    this.notifyParent(true);
  }

  private closeChat(): void {
    if (!this.opened() || this.closing()) return;

    this.closing.set(true);
    this.closeTimeoutId = setTimeout(() => {
      this.opened.set(false);
      this.closing.set(false);
    }, CLOSE_ANIMATION_MS);
    this.notifyParent(false);
  }

  private notifyParent(isOpen: boolean): void {
    // Target the embedder's origin derived from document.referrer; fall back
    // to '*' only when running standalone (no referrer = not inside an iframe).
    const targetOrigin = globalThis.document.referrer
      ? new URL(globalThis.document.referrer).origin
      : '*';
    try {
      globalThis.parent.postMessage(
        { type: isOpen ? 'bevvi:chat-opened' : 'bevvi:chat-closed' },
        targetOrigin,
      );
    } catch (err) {
      console.warn('[bevvi] postMessage failed:', err);
    }
  }
}
